import React, { useMemo } from "react"
import { Container, Row, Col } from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { DashStyles, DashHead, DateChip, PALETTE, useChartFix } from "../Dashboard/dashkit"

/*
 * HRMS Overview (static) — modelled on Anil's "HRM Dashboard" reference.
 * Workforce KPIs, working format, project vs bench, applications funnel, attendance,
 * staff turnover, recruitment pipeline. Numbers are placeholders.
 */
const P = PALETTE
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep"]

const KPIS = [
  { label: "Total Employees", value: "352", delta: "+15%", up: true, color: P.indigo, spark: [300, 310, 318, 325, 332, 338, 344, 352] },
  { label: "On Leave Today", value: "22", delta: "-10%", up: false, color: P.amber, spark: [30, 28, 27, 26, 25, 24, 23, 22] },
  { label: "New Joiners (MTD)", value: "32", delta: "+12%", up: true, color: P.green, spark: [18, 20, 22, 24, 26, 28, 30, 32] },
  { label: "Happiness Rate", value: "82%", delta: "-11%", up: false, color: P.cyan, spark: [88, 87, 86, 85, 84, 83, 82, 82] },
]

const RECRUITS = [
  { name: "Dom Sibley", dept: "DevOps", type: "Tech interview", c: P.indigo },
  { name: "Joe Root", dept: "UX/UI Designer", type: "Resume review", c: P.amber },
  { name: "Zak Crawley", dept: ".Net developer", type: "Final interview", c: P.green },
  { name: "Mark Wood", dept: "QA Engineer", type: "Screening", c: P.violet },
]

const HrmsOverview = ({ embedded = false }) => {
  if (!embedded) document.title = "HRMS Overview | Calyvern"
  useChartFix()

  const workingFormat = useMemo(() => ({
    options: {
      chart: { type: "donut", fontFamily: "inherit" },
      labels: ["Hybrid", "On-site", "Remote"], colors: [P.indigo, P.amber, P.green],
      legend: { show: false }, dataLabels: { enabled: false }, stroke: { width: 0 },
      plotOptions: { pie: { donut: { size: "72%", labels: { show: true, total: { show: true, label: "Total", fontSize: "12px", color: "#94a3b8", formatter: () => "100" }, value: { fontSize: "24px", fontWeight: 700, color: "#0f172a" } } } } },
    },
    series: [50, 20, 30],
  }), [])

  const projectEmployment = useMemo(() => ({
    options: {
      chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 3 } },
      colors: [P.indigo, "#c7d2fe"], dataLabels: { enabled: false }, legend: { show: false },
      xaxis: { categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], labels: { style: { colors: "#94a3b8" } } },
      yaxis: { labels: { style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    },
    series: [
      { name: "Project", data: [70, 90, 150, 200, 130, 220, 290] },
      { name: "Bench", data: [30, 25, 40, 30, 45, 35, 20] },
    ],
  }), [])

  const attendance = useMemo(() => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "62%", borderRadius: 2 } },
      colors: [P.indigo, P.amber, P.red], dataLabels: { enabled: false }, legend: { show: false },
      xaxis: { categories: MONTHS, labels: { style: { colors: "#94a3b8" } } },
      yaxis: { labels: { style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    },
    series: [
      { name: "On time", data: [300, 310, 290, 320, 315, 330, 325, 318, 340] },
      { name: "Late", data: [22, 18, 30, 15, 20, 12, 18, 22, 10] },
      { name: "Absent", data: [10, 14, 8, 12, 9, 7, 11, 8, 6] },
    ],
  }), [])

  const turnover = useMemo(() => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "50%", borderRadius: 3 } },
      colors: [P.violet], dataLabels: { enabled: false }, legend: { show: false },
      xaxis: { categories: MONTHS, labels: { style: { colors: "#94a3b8" } } },
      yaxis: { labels: { style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    },
    series: [{ name: "Exits", data: [8, 12, 18, 15, 23, 30, 27, 20, 14] }],
  }), [])

  const APP_FUNNEL = [["Applications", 58, P.indigo], ["Shortlisted", 22, P.green], ["On-hold", 12, P.amber], ["Rejected", 8, P.red]]

  return (
    <React.Fragment>
      <DashStyles />
      <div className={embedded ? "dk-wrap" : "page-content dk-wrap"}>
        <Container fluid>
          <DashHead title="HRMS Overview" welcome="Workforce, attendance and hiring at a glance." right={<DateChip />} />

          <Row className="g-3">
            {KPIS.map(k => (
              <Col key={k.label} md={6} xl={3}>
                <div className="dk-card dk-kpi">
                  <div className="dk-kpi-label">{k.label}</div>
                  <div className="dk-kpi-value">{k.value}</div>
                  <div className={`dk-kpi-delta ${k.up ? "up" : "down"}`}><i className={`bx ${k.up ? "bx-up-arrow-alt" : "bx-down-arrow-alt"}`} />{k.delta} vs last month</div>
                  <div className="dk-kpi-spark"><Sparklet data={k.spark} color={k.color} /></div>
                </div>
              </Col>
            ))}
          </Row>

          <Row className="g-3 mt-1">
            <Col xxl={4}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>WORKING FORMAT</span></div>
                <ReactApexChart options={workingFormat.options} series={workingFormat.series} type="donut" height={230} />
                <ul className="dk-list">
                  <li><span className="dk-dot" style={{ background: P.indigo }} /><span className="nm">Hybrid</span><span className="vl">50</span></li>
                  <li><span className="dk-dot" style={{ background: P.amber }} /><span className="nm">On-site</span><span className="vl">20</span></li>
                  <li><span className="dk-dot" style={{ background: P.green }} /><span className="nm">Remote</span><span className="vl">30</span></li>
                </ul>
              </div>
            </Col>
            <Col xxl={5}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>PROJECT vs BENCH (this week)</span></div>
                <div className="dk-legend"><span><i className="dk-dot" style={{ background: P.indigo }} />Project</span><span><i className="dk-dot" style={{ background: "#c7d2fe" }} />Bench</span></div>
                <ReactApexChart options={projectEmployment.options} series={projectEmployment.series} type="bar" height={250} />
              </div>
            </Col>
            <Col xxl={3}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>APPLICATIONS</span></div>
                <div style={{ marginTop: 6 }}>
                  {APP_FUNNEL.map(a => (
                    <div key={a[0]} style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 5 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 7, color: "#334155" }}><i className="dk-dot" style={{ background: a[2] }} />{a[0]}</span>
                        <b>{a[1]}%</b>
                      </div>
                      <div className="dk-progress"><span style={{ width: `${a[1]}%`, background: a[2] }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xxl={5}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>ATTENDANCE OVERVIEW</span><span className="dk-chip">Monthly</span></div>
                <div className="dk-legend"><span><i className="dk-dot" style={{ background: P.indigo }} />On time</span><span><i className="dk-dot" style={{ background: P.amber }} />Late</span><span><i className="dk-dot" style={{ background: P.red }} />Absent</span></div>
                <ReactApexChart options={attendance.options} series={attendance.series} type="bar" height={260} />
              </div>
            </Col>
            <Col xxl={3}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>STAFF TURNOVER</span></div>
                <ReactApexChart options={turnover.options} series={turnover.series} type="bar" height={260} />
              </div>
            </Col>
            <Col xxl={4}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>RECRUITMENT PROGRESS</span><span className="dk-link">View All →</span></div>
                <table className="dk-table">
                  <thead><tr><th>Name</th><th>Department</th><th>Stage</th></tr></thead>
                  <tbody>
                    {RECRUITS.map(r => (
                      <tr key={r.name}>
                        <td style={{ fontWeight: 600 }}>{r.name}</td>
                        <td style={{ color: "#64748b" }}>{r.dept}</td>
                        <td><span style={{ display: "flex", alignItems: "center", gap: 7 }}><i className="dk-dot" style={{ background: r.c }} />{r.type}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

const Sparklet = ({ data, color }) => (
  <ReactApexChart
    type="area" height={56}
    series={[{ data }]}
    options={{ chart: { sparkline: { enabled: true } }, stroke: { curve: "smooth", width: 2.5 }, colors: [color], fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] } }, tooltip: { enabled: false } }}
  />
)

export default HrmsOverview
